import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import RefreshControl from "@/components/RefreshControl";
import useAutoRefresh from "@/lib/useAutoRefresh";
import useBase44Sync from "@/lib/useBase44Sync";
import OrderMetaChips from "@/components/order/OrderMetaChips";
import { orderProgressPct, isOrderCompleted, isOrderCancelled, orderEffectiveStatus } from "@/lib/stages";
import { MagnifyingGlass } from "@phosphor-icons/react";
import SignedChip from "@/components/order/SignedChip";
import CancelledPill from "@/components/order/CancelledPill";
import OrderPaintChip from "@/components/order/OrderPaintChip";
import RevisionBadge from "@/components/order/RevisionBadge";

const TABS = [
  { key: "active", label: "Active" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
  { key: "all", label: "All" },
];

export default function Orders() {
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("status") || "active";
  const [tab, setTab] = useState(initialTab);
  const [search, setSearch] = useState(params.get("q") || "");
  const [storeFilter, setStoreFilter] = useState(params.get("store") || "");
  const [awaitingFilter, setAwaitingFilter] = useState(params.get("awaiting") || "");
  const [signedOnly, setSignedOnly] = useState(params.get("signed") === "1");
  const [signerFilter, setSignerFilter] = useState(params.get("signer") || "");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    try {
      // Explicitly request active, completed AND cancelled — the backend
      // defaults `/api/orders` to active-only, so fully-shipped or
      // cancelled orders fall off the list without explicit status params.
      const [activeRes, completedRes, cancelledRes] = await Promise.all([
        api.get("/orders", { params: { status: "active" } }).catch(() => ({ data: [] })),
        api.get("/orders", { params: { status: "completed" } }).catch(() => ({ data: [] })),
        api.get("/orders", { params: { status: "cancelled" } }).catch(() => ({ data: [] })),
      ]);
      const byId = new Map();
      for (const o of (activeRes.data || [])) if (o?.id) byId.set(o.id, o);
      for (const o of (completedRes.data || [])) if (o?.id) byId.set(o.id, o);
      for (const o of (cancelledRes.data || [])) if (o?.id) byId.set(o.id, o);
      setOrders(Array.from(byId.values()));
    } finally { setLoading(false); }
  }, []);

  const { refresh, refreshing, lastUpdatedAt } = useAutoRefresh(fetchOrders);
  const { runSync, syncing, canSync } = useBase44Sync();

  useEffect(() => {
    const next = new URLSearchParams(params);
    next.set("status", tab);
    if (search) next.set("q", search); else next.delete("q");
    if (storeFilter) next.set("store", storeFilter); else next.delete("store");
    if (awaitingFilter) next.set("awaiting", awaitingFilter); else next.delete("awaiting");
    if (signedOnly) next.set("signed", "1"); else next.delete("signed");
    if (signerFilter) next.set("signer", signerFilter); else next.delete("signer");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, search, storeFilter, awaitingFilter, signedOnly, signerFilter]);

  const stores = useMemo(() => {
    const set = new Set();
    orders.forEach((o) => { if (o.store) set.add(o.store); });
    return Array.from(set).sort();
  }, [orders]);

  const signers = useMemo(() => {
    const set = new Set();
    orders.forEach((o) => { if (o.signed_by) set.add(o.signed_by); });
    return Array.from(set).sort();
  }, [orders]);

  const filtered = orders.filter((o) => {
    const effectiveStatus = orderEffectiveStatus(o);
    if (tab !== "all" && effectiveStatus !== tab) return false;
    if (storeFilter && o.store !== storeFilter) return false;
    if (signedOnly && !o.signed_at && !o.signature_svg) return false;
    if (signerFilter && o.signed_by !== signerFilter) return false;
    if (awaitingFilter === "specialty") {
      if (!o.has_specialty_items) return false;
      if ((o.specialty_received_count || 0) >= (o.specialty_count || 0)) return false;
    }
    if (search) {
      const s = search.toLowerCase();
      return (
        o.order_number?.toLowerCase().includes(s) ||
        o.customer_name?.toLowerCase().includes(s) ||
        o.customer_address?.toLowerCase().includes(s) ||
        o.customer_email?.toLowerCase().includes(s) ||
        o.po_number?.toLowerCase().includes(s) ||
        o.store?.toLowerCase().includes(s)
      );
    }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">From Base44 · Live sync</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl mt-1">Orders</h1>
          <p className="text-sm text-zinc-600 mt-1">
            {orders.length} orders total · {orders.filter(o => !isOrderCompleted(o) && !isOrderCancelled(o)).length} active
          </p>
        </div>
        <RefreshControl
          onRefresh={refresh}
          refreshing={refreshing}
          lastUpdatedAt={lastUpdatedAt}
          onSync={canSync ? () => runSync(refresh) : null}
          syncing={syncing}
          testId="orders-refresh"
        />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-md text-xs uppercase tracking-[0.2em] font-bold border transition-colors ${
              tab === t.key
                ? "bg-zinc-900 text-white border-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 dark:border-zinc-100"
                : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
            }`}
          >
            {t.label}
          </button>
        ))}
        {stores.length > 0 && (
          <select
            value={storeFilter}
            onChange={(e) => setStoreFilter(e.target.value)}
            data-testid="orders-store-filter"
            className="h-10 px-3 rounded-md text-xs uppercase tracking-[0.15em] font-bold border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300 hover:border-zinc-400 cursor-pointer"
          >
            <option value="">All stores</option>
            {stores.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        )}
        {awaitingFilter === "specialty" && (
          <button
            type="button"
            onClick={() => setAwaitingFilter("")}
            data-testid="awaiting-filter-pill"
            className="h-10 px-3 rounded-md text-xs uppercase tracking-[0.15em] font-bold border border-purple-300 bg-purple-50 text-purple-800 hover:bg-purple-100 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800 inline-flex items-center gap-1.5"
          >
            Awaiting specialty <span aria-hidden>×</span>
          </button>
        )}
        <button
          type="button"
          onClick={() => setSignedOnly((v) => !v)}
          data-testid="signed-only-toggle"
          className={`h-10 px-3 rounded-md text-xs uppercase tracking-[0.15em] font-bold border inline-flex items-center gap-1.5 transition-colors ${
            signedOnly
              ? "border-emerald-400 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-700"
              : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300"
          }`}
        >
          Signed only {signedOnly && <span aria-hidden>×</span>}
        </button>
        {signers.length > 0 && (
          <select
            value={signerFilter}
            onChange={(e) => setSignerFilter(e.target.value)}
            data-testid="orders-signer-filter"
            className="h-10 px-3 rounded-md text-xs uppercase tracking-[0.15em] font-bold border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300 hover:border-zinc-400 cursor-pointer"
          >
            <option value="">All signers</option>
            {signers.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        )}
        <div className="relative flex-1 min-w-[220px] ml-auto">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" weight="bold" />
          <Input
            placeholder="Search order #, customer, email, PO…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-11"
            data-testid="orders-search-input"
          />
        </div>
      </div>

      {loading ? (
        <Card className="p-12 text-center text-zinc-500">Loading…</Card>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center text-zinc-500" data-testid="orders-empty">No orders match the filters.</Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((o) => {
            const progress = orderProgressPct(o);
            const effectiveStatus = orderEffectiveStatus(o);
            const cancelled = isOrderCancelled(o);
            return (
              <Link key={o.id} to={`/orders/${o.id}`} data-testid={`order-card-${o.order_number}`}>
                <Card
                  className={`p-5 transition-colors ${
                    cancelled
                      ? "border-zinc-200 border-l-4 border-l-red-600 opacity-90 hover:border-red-700"
                      : "border-zinc-200 hover:border-[#C8102E]"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Order</div>
                      <div className={`font-heading font-black text-xl truncate ${cancelled ? "line-through text-zinc-400" : ""}`}>
                        {o.order_number}
                      </div>
                      <div className={`text-sm truncate ${cancelled ? "text-zinc-500" : "text-zinc-700"}`}>{o.customer_name}</div>
                      {o.customer_address && (
                        <div className="text-xs text-zinc-500 truncate mt-0.5">{o.customer_address}</div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      {cancelled ? (
                        <CancelledPill order={o} />
                      ) : (
                        <span
                          className="text-[10px] uppercase tracking-[0.2em] font-bold rounded-md px-2 py-1 border whitespace-nowrap"
                          style={
                            effectiveStatus === "completed"
                              ? { background: "#D1FAE5", color: "#047857", borderColor: "#10B981" }
                              : { background: "#FEF3C7", color: "#92400E", borderColor: "#F59E0B" }
                          }
                        >
                          {effectiveStatus}
                        </span>
                      )}
                      <RevisionBadge order={o} />
                    </div>
                  </div>

                  {o.signed_at && !cancelled && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <SignedChip order={o} />
                      <OrderPaintChip order={o} />
                    </div>
                  )}
                  {!o.signed_at && !cancelled && Number(o.paint_out_count) > 0 && (
                    <div className="mt-2 flex flex-wrap">
                      <OrderPaintChip order={o} />
                    </div>
                  )}

                  <div className="mt-3">
                    <OrderMetaChips order={o} />
                  </div>

                  {!cancelled && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="uppercase tracking-widest text-zinc-500 font-semibold">
                          {o.completed_count}/{o.item_count} items · progress
                        </span>
                        <span className="font-mono font-semibold">{progress}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
                        <div className="h-full bg-[#C8102E]" style={{ width: `${progress}%` }} />
                      </div>
                    </div>
                  )}

                  {cancelled && (
                    <div className="mt-4 px-3 py-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-[11px] uppercase tracking-[0.15em] font-bold text-red-700 dark:text-red-300">
                      Do not pick / build / ship / print labels
                    </div>
                  )}
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
